/**
 * Admin panel API – all REST calls to backend.
 */

import { apiClient } from './client';
import type {
  User,
  Project,
  Device,
  ControlCategory,
  ControlButton,
  JoystickConfig,
  TeleportPoint,
} from '../types';
import type {
  AuthLoginRequest,
  AuthLoginResponse,
  UserCreateRequest,
  UserUpdateRequest,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  DeviceCreateRequest,
  DeviceUpdateRequest,
  ControlCategoryCreateRequest,
  ControlCategoryUpdateRequest,
  ControlButtonCreateRequest,
  ControlButtonUpdateRequest,
  FloorPlanGetResponse,
  FloorPlansListResponse,
  FloorPlanSetRequest,
  FloorPlanSetResponse,
  FloorPlanCreateRequest,
  FloorPlanCreateResponse,
  FloorPlanUpdateRequest,
  FloorPlanUpdateResponse,
  TeleportPointsListResponse,
  TeleportPointCreateRequest,
  TeleportPointUpdateRequest,
} from './contract';

export const adminApi = {
  // Auth (backend may not have this route yet – call will fail until implemented)
  login: (data: AuthLoginRequest) =>
    apiClient.post<AuthLoginResponse>('/auth/login', data),

  // Projects
  projects: {
    list: () => apiClient.get<Project[]>('/projects'),
    create: (data: ProjectCreateRequest) => apiClient.post<Project>('/projects', data),
    update: (id: string, data: ProjectUpdateRequest) =>
      apiClient.put<Project>(`/projects/${id}`, data),
    delete: (id: string) => apiClient.delete(`/projects/${id}`),
  },

  // Users
  users: {
    list: () => apiClient.get<User[]>('/users'),
    create: (data: UserCreateRequest) => apiClient.post<User>('/users', data),
    update: (id: string, data: UserUpdateRequest) =>
      apiClient.put<User>(`/users/${id}`, data),
    delete: (id: string) => apiClient.delete(`/users/${id}`),
  },

  // Devices
  devices: {
    list: (projectId?: string) =>
      apiClient.get<Device[]>('/devices', projectId ? { projectId } : undefined),
    /** Live device IDs connected via WebSocket relay (for status on Devices page). */
    getOnlineIds: () => apiClient.get<{ deviceIds: string[] }>('/devices/online-ids'),
    create: (data: DeviceCreateRequest) => apiClient.post<Device>('/devices', data),
    update: (id: string, data: DeviceUpdateRequest) =>
      apiClient.put<Device>(`/devices/${id}`, data),
    delete: (id: string) => apiClient.delete(`/devices/${id}`),
  },

  // Controls – Categories
  controls: {
    categories: {
      list: (projectId: string) =>
        apiClient.get<ControlCategory[]>('/controls/categories', { projectId }),
      create: (data: ControlCategoryCreateRequest) =>
        apiClient.post<ControlCategory>('/controls/categories', data),
      update: (id: string, data: ControlCategoryUpdateRequest) =>
        apiClient.put<ControlCategory>(`/controls/categories/${id}`, data),
      delete: (id: string) => apiClient.delete(`/controls/categories/${id}`),
    },
    buttons: {
      listByCategory: (categoryId: string) =>
        apiClient.get<ControlButton[]>('/controls/buttons', { categoryId }),
      listByProject: (projectId: string) =>
        apiClient.get<ControlButton[]>('/controls/buttons', { projectId }),
      create: (data: ControlButtonCreateRequest) =>
        apiClient.post<ControlButton>('/controls/buttons', data),
      update: (id: string, data: ControlButtonUpdateRequest) =>
        apiClient.put<ControlButton>(`/controls/buttons/${id}`, data),
      delete: (id: string) => apiClient.delete(`/controls/buttons/${id}`),
    },
    joystickConfig: {
      get: (projectId: string) =>
        apiClient.get<JoystickConfig>('/controls/joystick-config', { projectId }),
      update: (projectId: string, data: JoystickConfig) =>
        apiClient.put<JoystickConfig>('/controls/joystick-config', data, { projectId }),
    },
  },

  // Floor plans & teleport points
  floorPlans: {
    listByProject: (projectId: string) =>
      apiClient.get<FloorPlansListResponse>(`/projects/${projectId}/floorplans`),
    getByProject: (projectId: string) =>
      apiClient.get<FloorPlanGetResponse>(`/projects/${projectId}/floorplan`),
    createForProject: (projectId: string, data: FloorPlanCreateRequest) =>
      apiClient.post<FloorPlanCreateResponse>(`/admin/projects/${projectId}/floorplans`, data),
    setForProject: (projectId: string, data: FloorPlanSetRequest) =>
      apiClient.post<FloorPlanSetResponse>(`/admin/projects/${projectId}/floorplan`, data),
    update: (id: string, data: FloorPlanUpdateRequest) =>
      apiClient.put<FloorPlanUpdateResponse>(`/admin/floorplans/${id}`, data),
    delete: (id: string) => apiClient.delete(`/admin/floorplans/${id}`),
    uploadForProject: (projectId: string, file: File, name?: string) => {
      const form = new FormData();
      form.append('image', file);
      if (name && name.trim()) form.append('name', name.trim());
      return apiClient.postForm<FloorPlanCreateResponse>(
        `/admin/projects/${projectId}/floorplans/upload`,
        form
      );
    },
    uploadById: (id: string, file: File, name?: string) => {
      const form = new FormData();
      form.append('image', file);
      if (name && name.trim()) form.append('name', name.trim());
      return apiClient.postForm<FloorPlanUpdateResponse>(`/admin/floorplans/${id}/upload`, form);
    },
  },
  teleportPoints: {
    listByProject: (projectId: string, floorPlanId?: string) =>
      apiClient.get<TeleportPointsListResponse>(
        `/projects/${projectId}/points`,
        floorPlanId ? { floorPlanId } : undefined
      ),
    create: (floorPlanId: string, data: TeleportPointCreateRequest) =>
      apiClient.post<TeleportPoint>(`/admin/floorplan/${floorPlanId}/points`, data),
    update: (id: string, data: TeleportPointUpdateRequest) =>
      apiClient.put<TeleportPoint>(`/admin/points/${id}`, data),
    delete: (id: string) => apiClient.delete(`/admin/points/${id}`),
    uploadIcon: (id: string, file: File) => {
      const form = new FormData();
      form.append('icon', file);
      return apiClient.postForm<TeleportPoint>(`/admin/points/${id}/icon`, form);
    },
  },
};

